import React, { useState, useEffect } from "react";
import { 
  Paper, 
  Typography, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box, 
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import LoopIcon from '@mui/icons-material/Loop';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import TextFieldsIcon from '@mui/icons-material/TextFields';

const StepEditor = ({ step, onSave, onCancel }) => {
  const [editedStep, setEditedStep] = useState({
    type: "action",
    action: "",
    selector: "",
    value: "",
    waitFor: 5,
    successCriteria: {
      elementVisible: false,
      elementVisibleSelector: "",
      textPresent: false,
      textPresentValue: "",
      urlContains: false,
      urlContainsValue: "",
    },
    loopType: "",
    loopConfig: {
      collection: "",
      condition: "",
      count: 1,
      start: 0,
      end: 10,
      step: 1,
      itemVariable: "",
      indexVariable: ""
    },
    conditionType: "",
    conditionConfig: {
      condition: "",
      switchVariable: "",
      cases: [],
      thenSteps: [],
      elseSteps: []
    },
    variableType: "",
    variableConfig: {
      name: "",
      initialValue: "",
      scope: "automation"
    },
    ...step
  });

  useEffect(() => {
    setEditedStep({
      type: "action",
      action: "",
      selector: "",
      value: "",
      waitFor: 5,
      successCriteria: {
        elementVisible: false,
        elementVisibleSelector: "",
        textPresent: false,
        textPresentValue: "",
        urlContains: false,
        urlContainsValue: "",
      },
      loopType: "",
      loopConfig: {
        collection: "",
        condition: "",
        count: 1,
        start: 0,
        end: 10,
        step: 1,
        itemVariable: "",
        indexVariable: ""
      },
      conditionType: "",
      conditionConfig: {
        condition: "",
        switchVariable: "",
        cases: [],
        thenSteps: [],
        elseSteps: []
      },
      variableType: "",
      variableConfig: {
        name: "",
        initialValue: "",
        scope: "automation"
      },
      ...step
    });
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedStep(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSuccessCriteriaChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    setEditedStep(prev => ({
      ...prev,
      successCriteria: {
        ...prev.successCriteria,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleLoopConfigChange = (e) => {
    const { name, value } = e.target;
    
    setEditedStep(prev => ({
      ...prev,
      loopConfig: {
        ...prev.loopConfig,
        [name]: name === 'count' || name === 'start' || name === 'end' || name === 'step' 
          ? parseInt(value, 10) 
          : value
      }
    }));
  };

  const handleConditionConfigChange = (e) => {
    const { name, value } = e.target;
    
    setEditedStep(prev => ({
      ...prev,
      conditionConfig: {
        ...prev.conditionConfig,
        [name]: value
      }
    }));
  };

  const handleVariableConfigChange = (e) => {
    const { name, value } = e.target;
    
    setEditedStep(prev => ({
      ...prev,
      variableConfig: {
        ...prev.variableConfig,
        [name]: value
      }
    }));
  };

  const handleAddCase = () => {
    setEditedStep(prev => ({
      ...prev,
      conditionConfig: {
        ...prev.conditionConfig,
        cases: [
          ...prev.conditionConfig.cases,
          { value: "", steps: [] }
        ]
      }
    }));
  };

  const handleCaseChange = (index, value) => {
    setEditedStep(prev => {
      const newCases = [...prev.conditionConfig.cases];
      newCases[index] = { ...newCases[index], value };
      return {
        ...prev,
        conditionConfig: {
          ...prev.conditionConfig,
          cases: newCases
        }
      };
    });
  };

  const handleRemoveCase = (index) => {
    setEditedStep(prev => {
      const newCases = [...prev.conditionConfig.cases];
      newCases.splice(index, 1);
      return {
        ...prev,
        conditionConfig: {
          ...prev.conditionConfig,
          cases: newCases
        }
      };
    });
  };

  const handleSave = () => {
    onSave(editedStep);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Step Editor
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Step Type</InputLabel>
            <Select
              name="type"
              value={editedStep.type}
              onChange={handleChange}
              label="Step Type"
            >
              <MenuItem value="action">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  👆
                </Box>
                <ListItemText>Action</ListItemText>
              </MenuItem>
              <MenuItem value="loop">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  <LoopIcon />
                </Box>
                <ListItemText>Loop</ListItemText>
              </MenuItem>
              <MenuItem value="condition">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  <CallSplitIcon />
                </Box>
                <ListItemText>Condition</ListItemText>
              </MenuItem>
              <MenuItem value="variable">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  <TextFieldsIcon />
                </Box>
                <ListItemText>Variable</ListItemText>
              </MenuItem>
              <MenuItem value="wait">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  ⏱️
                </Box>
                <ListItemText>Wait</ListItemText>
              </MenuItem>
              <MenuItem value="assertion">
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                  ✅
                </Box>
                <ListItemText>Assertion</ListItemText>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        {/* Action Type Fields */}
        {editedStep.type === "action" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Action</InputLabel>
                <Select
                  name="action"
                  value={editedStep.action}
                  onChange={handleChange}
                  label="Action"
                >
                  <MenuItem value="navigate">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                      🧭
                    </Box>
                    <ListItemText>Navigate</ListItemText>
                  </MenuItem>
                  <MenuItem value="click">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                      👆
                    </Box>
                    <ListItemText>Click</ListItemText>
                  </MenuItem>
                  <MenuItem value="type">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                      ✏️
                    </Box>
                    <ListItemText>Type</ListItemText>
                  </MenuItem>
                  <MenuItem value="select">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 24, mr: 2, fontSize: '18px' }}>
                      🔍
                    </Box>
                    <ListItemText>Select</ListItemText>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {editedStep.action === "navigate" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="URL"
                  name="value"
                  value={editedStep.value || ""}
                  onChange={handleChange}
                />
              </Grid>
            )}
            
            {(editedStep.action === "click" || editedStep.action === "type" || editedStep.action === "select") && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Selector"
                  name="selector"
                  value={editedStep.selector || ""}
                  onChange={handleChange}
                />
              </Grid>
            )}
            
            {editedStep.action === "type" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Value"
                  name="value"
                  value={editedStep.value || ""}
                  onChange={handleChange}
                />
              </Grid>
            )}
          </>
        )}
        
        {/* Loop Type Fields */}
        {editedStep.type === "loop" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Loop Type</InputLabel>
                <Select
                  name="loopType"
                  value={editedStep.loopType}
                  onChange={handleChange}
                  label="Loop Type"
                >
                  <MenuItem value="for_each">For Each</MenuItem>
                  <MenuItem value="while">While</MenuItem>
                  <MenuItem value="repeat">Repeat</MenuItem>
                  <MenuItem value="for_range">For Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {editedStep.loopType === "for_each" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Collection Variable"
                    name="collection"
                    value={editedStep.loopConfig.collection || ""}
                    onChange={handleLoopConfigChange}
                    helperText="Variable name containing the collection to iterate over"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Item Variable"
                    name="itemVariable"
                    value={editedStep.loopConfig.itemVariable || ""}
                    onChange={handleLoopConfigChange}
                    helperText="Variable name for the current item"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Index Variable (optional)"
                    name="indexVariable"
                    value={editedStep.loopConfig.indexVariable || ""}
                    onChange={handleLoopConfigChange}
                    helperText="Variable name for the current index"
                  />
                </Grid>
              </>
            )}
            
            {editedStep.loopType === "while" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Condition"
                  name="condition"
                  value={editedStep.loopConfig.condition || ""}
                  onChange={handleLoopConfigChange}
                  helperText="JavaScript condition that evaluates to true/false"
                />
              </Grid>
            )}
            
            {editedStep.loopType === "repeat" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Count"
                  name="count"
                  type="number"
                  value={editedStep.loopConfig.count || 1}
                  onChange={handleLoopConfigChange}
                  helperText="Number of times to repeat"
                />
              </Grid>
            )}
            
            {editedStep.loopType === "for_range" && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Start"
                    name="start"
                    type="number"
                    value={editedStep.loopConfig.start || 0}
                    onChange={handleLoopConfigChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="End"
                    name="end"
                    type="number"
                    value={editedStep.loopConfig.end || 10}
                    onChange={handleLoopConfigChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Step"
                    name="step"
                    type="number"
                    value={editedStep.loopConfig.step || 1}
                    onChange={handleLoopConfigChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Index Variable"
                    name="indexVariable"
                    value={editedStep.loopConfig.indexVariable || ""}
                    onChange={handleLoopConfigChange}
                    helperText="Variable name for the current index"
                  />
                </Grid>
              </>
            )}
          </>
        )}
        
        {/* Condition Type Fields */}
        {editedStep.type === "condition" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Condition Type</InputLabel>
                <Select
                  name="conditionType"
                  value={editedStep.conditionType}
                  onChange={handleChange}
                  label="Condition Type"
                >
                  <MenuItem value="if">If</MenuItem>
                  <MenuItem value="if_else">If-Else</MenuItem>
                  <MenuItem value="switch">Switch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {(editedStep.conditionType === "if" || editedStep.conditionType === "if_else") && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Condition"
                  name="condition"
                  value={editedStep.conditionConfig.condition || ""}
                  onChange={handleConditionConfigChange}
                  helperText="JavaScript condition that evaluates to true/false"
                />
              </Grid>
            )}
            
            {editedStep.conditionType === "switch" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Switch Variable"
                    name="switchVariable"
                    value={editedStep.conditionConfig.switchVariable || ""}
                    onChange={handleConditionConfigChange}
                    helperText="Variable to switch on"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Cases
                  </Typography>
                  {editedStep.conditionConfig.cases.map((caseItem, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        label={`Case ${index + 1}`}
                        value={caseItem.value}
                        onChange={(e) => handleCaseChange(index, e.target.value)}
                      />
                      <IconButton 
                        color="error" 
                        onClick={() => handleRemoveCase(index)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button 
                    startIcon={<AddIcon />} 
                    variant="outlined" 
                    size="small" 
                    onClick={handleAddCase}
                    sx={{ mt: 1 }}
                  >
                    Add Case
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
        
        {/* Variable Type Fields */}
        {editedStep.type === "variable" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Variable Type</InputLabel>
                <Select
                  name="variableType"
                  value={editedStep.variableType}
                  onChange={handleChange}
                  label="Variable Type"
                >
                  <MenuItem value="string">String</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="boolean">Boolean</MenuItem>
                  <MenuItem value="array">Array</MenuItem>
                  <MenuItem value="object">Object</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Variable Name"
                name="name"
                value={editedStep.variableConfig.name || ""}
                onChange={handleVariableConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Initial Value"
                name="initialValue"
                value={editedStep.variableConfig.initialValue || ""}
                onChange={handleVariableConfigChange}
                multiline={editedStep.variableType === "array" || editedStep.variableType === "object"}
                rows={editedStep.variableType === "array" || editedStep.variableType === "object" ? 4 : 1}
                helperText={
                  editedStep.variableType === "array" 
                    ? "Enter as JSON array: [1, 2, 3]" 
                    : editedStep.variableType === "object" 
                      ? "Enter as JSON object: {\"key\": \"value\"}" 
                      : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Scope</InputLabel>
                <Select
                  name="scope"
                  value={editedStep.variableConfig.scope}
                  onChange={handleVariableConfigChange}
                  label="Scope"
                >
                  <MenuItem value="global">Global</MenuItem>
                  <MenuItem value="automation">Automation</MenuItem>
                  <MenuItem value="step">Step</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        
        {/* Wait Type Fields */}
        {editedStep.type === "wait" && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Wait for (seconds)"
              name="waitFor"
              type="number"
              value={editedStep.waitFor || 5}
              onChange={handleChange}
            />
          </Grid>
        )}
      </Grid>
      
      {/* Success Criteria Section */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Success Criteria</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Checkbox
                checked={editedStep.successCriteria?.elementVisible || false}
                onChange={handleSuccessCriteriaChange}
                name="elementVisible"
              />
            }
            label="Element visible"
          />
          
          {editedStep.successCriteria?.elementVisible && (
            <TextField
              fullWidth
              margin="normal"
              label="Element Selector"
              name="elementVisibleSelector"
              value={editedStep.successCriteria?.elementVisibleSelector || ""}
              onChange={handleSuccessCriteriaChange}
            />
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={editedStep.successCriteria?.textPresent || false}
                onChange={handleSuccessCriteriaChange}
                name="textPresent"
              />
            }
            label="Text present"
          />
          
          {editedStep.successCriteria?.textPresent && (
            <TextField
              fullWidth
              margin="normal"
              label="Text to Check"
              name="textPresentValue"
              value={editedStep.successCriteria?.textPresentValue || ""}
              onChange={handleSuccessCriteriaChange}
            />
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={editedStep.successCriteria?.urlContains || false}
                onChange={handleSuccessCriteriaChange}
                name="urlContains"
              />
            }
            label="URL contains"
          />
          
          {editedStep.successCriteria?.urlContains && (
            <TextField
              fullWidth
              margin="normal"
              label="URL Pattern"
              name="urlContainsValue"
              value={editedStep.successCriteria?.urlContainsValue || ""}
              onChange={handleSuccessCriteriaChange}
            />
          )}
        </AccordionDetails>
      </Accordion>
      
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button variant="outlined" onClick={onCancel} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save Step
        </Button>
      </Box>
    </Paper>
  );
};

export default StepEditor;
